import { useCallback, useEffect, useMemo, useState, useRef } from 'react';

export const useFilterAndBlur = ({
  dataService,
  originalImage,
  setLoadingStatus,
  initialFilter,
  initialBlur,
  isActive,
}) => {
  const {
    imagesWithFilter,
    imagesWithFilterAndBlur,
    getImagesWithFilter,
    getImagesWithFilterAndBlur,
  } = dataService;

  const [filterIndex, setFilterIndex] = useState(0);
  const [backgroundBlurEnabled, setBackgroundBlurEnabled] = useState(false);
  const [imagesWithFilterAndBlurCached, setImagesWithFilterAndBlurCached] = useState([]);
  const [backgroundBlurLevel, setBackgroundBlurLevel] = useState(50);
  const [currentImage, setCurrentImage] = useState('');
  const [initialStateApplied, setInitialStateApplied] = useState(false);

  const initialStatusWhenActive = useRef();

  useEffect(() => {
    if (isActive) {
      initialStatusWhenActive.current = {
        filterIndex,
        backgroundBlurLevel,
        backgroundBlurEnabled,
      };
    } else {
      initialStatusWhenActive.current = null;
    }
  }, [isActive, filterIndex, backgroundBlurLevel, backgroundBlurEnabled]);

  useEffect(() => {
    if (imagesWithFilter && imagesWithFilter.length > 0 && !initialStateApplied && (initialFilter || initialBlur)) {
      if (initialFilter) {
        const index = imagesWithFilter.findIndex(image => image.filter === initialFilter);
        if (index !== -1) {
          setFilterIndex(index);
        }
      }
      if (initialBlur) {
        setBackgroundBlurEnabled(true);
        setBackgroundBlurLevel(initialBlur);
        getImagesWithFilterAndBlur(originalImage, initialBlur);
      }
      setInitialStateApplied(true);
    }
  }, [getImagesWithFilterAndBlur, imagesWithFilter, imagesWithFilterAndBlur, initialBlur, initialFilter, initialStateApplied, originalImage]);

  // update image when image changes. e.g., background removal
  const updateImage = useCallback((newImage) => {
    setCurrentImage(newImage.url);
    if (backgroundBlurEnabled) {
      getImagesWithFilter(newImage.url);
      return getImagesWithFilterAndBlur(newImage.url, backgroundBlurLevel);
    }
    setImagesWithFilterAndBlurCached([]);
    return getImagesWithFilter(newImage.url);
  }, [backgroundBlurEnabled, backgroundBlurLevel, getImagesWithFilter, getImagesWithFilterAndBlur]);

  const resultImage = useMemo(() => {
    if (backgroundBlurEnabled && imagesWithFilterAndBlurCached.length) {
      return imagesWithFilterAndBlurCached[filterIndex];
    } else if (imagesWithFilter && imagesWithFilter.length) {
      return imagesWithFilter[filterIndex];
    }

    return ({
      filter: null,
      url: originalImage,
    });
  }, [backgroundBlurEnabled, filterIndex, imagesWithFilter, imagesWithFilterAndBlurCached, originalImage]);

  const getImagesWithFilterImp = useCallback((image) => {
    setLoadingStatus(true);
    getImagesWithFilter(image).finally(() => {
      setLoadingStatus(false);
    });
  }, [getImagesWithFilter, setLoadingStatus]);

  useEffect(() => {
    if (imagesWithFilterAndBlur) {
      setImagesWithFilterAndBlurCached(imagesWithFilterAndBlur);
    }
  }, [imagesWithFilterAndBlur]);

  useEffect(() => {
    if (originalImage) {
      setCurrentImage(originalImage);
      setLoadingStatus(true);
      getImagesWithFilter(originalImage).finally(() => {
        setLoadingStatus(false);
      });
    }
  }, [getImagesWithFilter, originalImage, setLoadingStatus]);

  const toggleBackgroundBlur = useCallback((enableBackgroundBlur) => {
    if (enableBackgroundBlur) {
      if (!imagesWithFilterAndBlurCached.length) {
        setLoadingStatus(true);
        getImagesWithFilterAndBlur(currentImage, backgroundBlurLevel).finally(() => {
          setLoadingStatus(false);
        });
      }
    }
    setBackgroundBlurEnabled(enableBackgroundBlur);
  }, [backgroundBlurLevel, currentImage, getImagesWithFilterAndBlur, imagesWithFilterAndBlurCached.length, setLoadingStatus]);

  const changeBackgroundBlurLevel = useCallback((blurLevel = 50) => {
    setLoadingStatus(true);
    setBackgroundBlurLevel(blurLevel);
    getImagesWithFilterAndBlur(currentImage, blurLevel).finally(() => {
      setLoadingStatus(false);
    });
  }, [currentImage, getImagesWithFilterAndBlur, setLoadingStatus]);

  const isImageUpdated = useMemo(() => {
    if (initialBlur && initialFilter && initialStateApplied) {
      return !backgroundBlurEnabled || initialBlur !== backgroundBlurLevel || initialFilter !== imagesWithFilter[filterIndex].filter;
    } else if (initialBlur && initialStateApplied) {
      return !backgroundBlurEnabled || initialBlur !== backgroundBlurLevel || filterIndex !== 0;
    } else if (initialFilter && initialStateApplied) {
      return initialFilter !== imagesWithFilter[filterIndex].filter || backgroundBlurEnabled;
    }
    return resultImage && resultImage.url !== originalImage;
  }, [backgroundBlurEnabled, backgroundBlurLevel, filterIndex, imagesWithFilter, initialBlur, initialFilter, initialStateApplied, originalImage, resultImage]);

  // check if image is changed when current view is active
  // maybe is same as original image but different from the one when user landed on this view
  const isImageChangedWhenActive = useMemo(
    () =>
      initialStatusWhenActive.current &&
      (initialStatusWhenActive.current.filterIndex !== filterIndex
      || initialStatusWhenActive.current.backgroundBlurLevel !== backgroundBlurLevel
      || initialStatusWhenActive.current.backgroundBlurEnabled !== backgroundBlurEnabled),
    [backgroundBlurEnabled, backgroundBlurLevel, filterIndex]
  );

  return {
    filterIndex,
    setFilterIndex,
    updateImage,
    backgroundBlurEnabled,
    toggleBackgroundBlur,
    changeBackgroundBlurLevel,
    backgroundBlurLevel,
    resultImage,
    imagesWithFilter,
    getImagesWithFilter: getImagesWithFilterImp,
    isImageUpdated,
    isImageChangedWhenActive,
  };
};
